import React, { useState, useEffect } from 'react';
import { helmetHiddenPowerData, chestplateHiddenPowerData, leggingsHiddenPowerData, weaponHiddenPowerData } from '../data/hiddenPowerData';
import HiddenPowerDropdown from "./hiddenPowerDropdown";
import renderStats from './renderStats';
import '../css/gear.css';
import LRUCache from '../components/cache.js';

export function GearDisplay({
  type,
  piece,
  hiddenPowers,
  onPieceChange,
  onHiddenPowerChange,
  isSetActive,
  context
}) {
  const [gearList, setGearList] = useState([]);
  const [showIncompatible, setShowIncompatible] = useState(false);

  
  //runs a single time, since gear list is not gonna change
  useEffect(() => {
    const fetchGear = async () => {
      const list = await getGearList(type);
      setGearList(list);
    };

    fetchGear();
  }, []);
  async function getGearList(type) {
    const res = await fetch(`/api/equipment/getGearList/${type.toLowerCase()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!res.ok) {
      console.error('Failed to fetch gear list');
      return [];
    }

    return await res.json();
  }

  const handleHiddenPowerChange = (index, value) => {
    onHiddenPowerChange(index, value);
  };
  const [dropdownOpen, setDropdownOpen] = useState(false);

  //Slot count for hidden powers is based on gear Rarity
  const slotCount = ['super epic', 'unique', 'limited'].includes(piece.rarity?.toLowerCase?.())
  ? 2
  : piece.rarity?.toLowerCase?.() === 'epic'
  ? 1
  : 0;

  const getHiddenPowerData = (type) => {
    switch (type.toLowerCase()) {
      case 'helmet':
        return helmetHiddenPowerData;
      case 'weapon':
        return weaponHiddenPowerData;
      case 'chestplate':
        return chestplateHiddenPowerData;
      case 'leggings':
        return leggingsHiddenPowerData;
      default:
        return null;
    }
  };


  const grabImg = (piece) => {
    const imgName = piece.name.replace(/ /g, "_");
    return `/img/Equipment/${type}/${imgName}.webp`;
  };


  // Updates star count and re-fetches piece
  const updateStarCount = (newCount) => {
    const clamped = Math.max(1, Math.min(5, newCount));
    piece.stars = clamped;
    selectPiece(piece.name);
  };


  const gearCache = new LRUCache(50);
  //API call for grabbing new piece
  const selectPiece = async (name) => {
    try {
      const payload = {
        type,
        name,
        level: 60,
        stars: piece.stars
      };
      const cacheKey = JSON.stringify(payload);

      const cachedGear = gearCache.get(cacheKey);
      if (cachedGear) {
        onPieceChange(cachedGear);
        setDropdownOpen(false);
        handleHiddenPowers(cachedGear.rarity);
        return;
      }

      const res = await fetch('/api/equipment/getPiece', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: cacheKey
      });

      if (!res.ok) throw new Error(`Gear fetch failed, ${JSON.stringify({ type, name })}`);

      const gear = await res.json();
      gearCache.set(cacheKey, gear);

      onPieceChange(gear);
      setDropdownOpen(false);
      handleHiddenPowers(gear.rarity);

    } catch (err) {
      console.error(err);
    }
  };

  // Clear hidden powers that don't exist anymore
  const handleHiddenPowers = (rarity) => {
    if (!['Unique', 'Super Epic', 'Limited'].includes(rarity)) {
      handleHiddenPowerChange(1, null);
    } else if (!['Epic'].includes(rarity)) {
      handleHiddenPowerChange(1, null);
      handleHiddenPowerChange(0, null);
    }
  };



  const getSetBonus = (gear) => {
    let stats = {};
    if (gear.setBonus) {
      gear.setBonus.stats.forEach(stat => {
        if (!stats[stat.key]) stats[stat.key] = { value: 0 };
        stats[stat.key].value += stat.value;
        if (stat.target) stats[stat.key].target = stat.target;
      });
    }
    return stats;
  };

  

  const hiddenPowerData = getHiddenPowerData(piece.type);

  const baseStats = piece.baseStats || {};
  const uniqueStats = piece.uniqueStats || {};
  const setBonus = getSetBonus(piece);

  return (
    <div className="gearContainerWrapper">
      <div className={`gearContainer ${type.toLowerCase()}`}>
        <div className={`gearHeader`}>{type}</div>

        {/* Piece name and image */}
        <div className='pieceName'>{piece.name}</div>
        <div className={`imageContainer rarityBgSoft rarityBorder ${piece.rarity.toLowerCase()}`}>
          <img
            src={grabImg(piece)}
            alt={piece.name}
            className={`gearImage rarityBg rarityBorder ${piece.rarity.toLowerCase()}`}
            onClick={() => setDropdownOpen(true)} // Open modal on image click
            style={{ cursor: 'pointer' }}
          />
          {/* Star display and increments */}
          <div className="starControls">
            <button onClick={() => updateStarCount(piece.stars - 1)}>−</button>
            {[...Array(5)].map((_, index) => (
              <img 
              src="/img/Icons/Other/Star.webp"
              key={index}
              alt="star"
              className={`star ${index < piece.stars ? `filled` : 'empty'}`}
              ></img>
            ))}
            <button onClick={() => updateStarCount(piece.stars + 1)}>+</button>
          </div>
        </div>
        
        {/* Stat display */}
        <div className="statContainer">
          {/* Base Stats */}
          <div className="baseStats">Base Stats</div>
          {renderStats(baseStats)}

          {/* Unique Stats (Star based) */}
          <div className="baseStats active">Unique Ability</div>
          {renderStats(uniqueStats)}

          {/* Set Bonus */}
          {setBonus && Object.keys(setBonus).length > 0 && (
            <>
              <div className={`baseStats ${piece.setKey} ${isSetActive ? 'active' : ''}`}>
                {piece.setName}
              </div>
              {!isSetActive && <div>Requires {piece.setBonus.pieces} pieces</div>}
              {renderStats(setBonus)}

              {/* Set bonus buffs/debuffs */}
              {piece.setBonus?.buffs && piece.setBonus.buffs.map((buff, index) => (
                <div key={`buff-${index}`} className="buffText">
                  {buff.text}
                </div>
              ))}
            </>
          )}


          {/* Hidden Powers */}
        </div>
        
        {/* Hidden Powers */}
        {slotCount > 0 ? (
          
          <>
            <div className="statContainer">
              <div className="baseStats">Hidden Powers</div>
            </div>

            <HiddenPowerDropdown
              data={hiddenPowerData}
              value={hiddenPowers[0]}
              onChange={(e) => handleHiddenPowerChange(0, e)}
              placeholder="Value"
            />

            {slotCount === 2 && (
              <HiddenPowerDropdown
                data={hiddenPowerData}
                value={hiddenPowers[1]}
                onChange={(e) => handleHiddenPowerChange(1, e)}
                placeholder="Value"
              />
            )}
          </>
        ) : (
           <div style={{ height: '6px' }} /> 
        )}
      </div>

      {/* Gear Selector Modal */}
      {dropdownOpen && (
        <div className="gearModalOverlay" onClick={() => setDropdownOpen(false)}>
          <div className="gearModalContent" onClick={(e) => e.stopPropagation()}>
            
            {/* Checkbox to toggle showing incompatible gear */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                checked={showIncompatible}
                onChange={(e) => setShowIncompatible(e.target.checked)}
              />
              Show Incompatible Gear
            </label>

            <div
              className="gearDropdown"
            >
              {gearList
                .filter((option) => {
                  if (showIncompatible) return true;

                  return option.required == null || option.required === context.name || option.required === context.element || option.required === context.class;
                })
                .map((option) => (
                  <div
                    key={option.name}
                    className="gearImageWrapper"
                    style={{
                      border: option.name === piece.name ? '2px solid green' : 'none',
                    }}
                  >
                    <img
                      src={grabImg(option)}
                      alt={option.name}
                      className={`dropdownImage rarityBg rarityBorder ${option.rarity?.toLowerCase()}`}
                      style={{
                        width: '60px',
                        height: '60px',
                        cursor: 'pointer',
                        display: 'flex',
                      }}
                      onClick={() => selectPiece(option.name)}
                    />
                    {/* Hover tooltip with Gear Name */}
                    <div className="gearName">{option.name}</div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
